import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Clock, HardHat, Fingerprint, LogIn, LogOut as LogOutIcon, AlertTriangle, Euro } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { requestBiometric } from "@/lib/biometric";
import QuickActions from "@/components/QuickActions";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

export default function OperaioHome() {
  const [cantieri, setCantieri] = useState([]);
  const [cantiereId, setCantiereId] = useState("");
  const [myPresenze, setMyPresenze] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [busy, setBusy] = useState(false);
  const [anom, setAnom] = useState({ open: false, tid: null, msg: "" });
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    const [c, p, t] = await Promise.all([api.get("/cantieri"), api.get("/presenze"), api.get("/timbrature")]);
    setCantieri(c.data); setMyPresenze(p.data.slice(0, 10)); setTimbrature(t.data.slice(0, 20));
  };
  useEffect(() => { load(); }, []);

  const timbraBiometrica = async (tipo) => {
    if (!cantiereId && tipo === "entrata") return toast.error("Seleziona cantiere prima di entrare");
    setBusy(true);
    toast.info("Avvicina il dito al sensore o usa il PIN...");
    const ok = await requestBiometric(user.id, user.name || user.email);
    if (!ok) {
      const conferma = window.confirm("Biometria non disponibile su questo dispositivo. Confermi la timbratura?");
      if (!conferma) { setBusy(false); return; }
    }
    try {
      await api.post("/timbrature", {
        cantiere_id: tipo === "entrata" ? cantiereId : (lastTimbr()?.cantiere_id || cantiereId),
        tipo, biometric_verified: ok,
      });
      toast.success(`${tipo === "entrata" ? "Entrata" : "Uscita"} registrata ${ok ? "✓ impronta" : ""}`);
      load();
    } catch { toast.error("Errore timbratura"); }
    finally { setBusy(false); }
  };

  const sendAnomalia = async () => {
    try {
      await api.post(`/timbrature/${anom.tid}/segnala`, { messaggio: anom.msg });
      toast.success("Segnalazione inviata all'amministratore");
      setAnom({ open: false, tid: null, msg: "" }); load();
    } catch { toast.error("Errore"); }
  };

  const lastTimbr = () => timbrature[0];
  const todayTimbr = timbrature.filter((t) => t.data === today);
  const lastEntrata = todayTimbr.find((t) => t.tipo === "entrata");
  const lastUscita = todayTimbr.find((t) => t.tipo === "uscita");

  const cmap = Object.fromEntries(cantieri.map((c) => [c.id, c.nome]));
  const oggi = myPresenze.filter((p) => p.data === today);
  const oreOggi = oggi.reduce((s, p) => s + (p.ore || 0), 0);
  const pagaOggi = oggi.reduce((s, p) => s + (p.paga_totale || 0), 0);
  const totMese = myPresenze.reduce((s, p) => s + (p.paga_totale || 0), 0);
  const totPagato = myPresenze.reduce((s, p) => s + (p.importo_pagato || 0), 0);

  return (
    <div className="max-w-md mx-auto space-y-6">
      <div className="text-center space-y-1">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Buongiorno</div>
        <h1 className="text-3xl font-extrabold tracking-tight">{user?.name}</h1>
        <div className="text-sm text-muted-foreground">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
      </div>

      {user?.role === "capocantiere" && <QuickActions />}

      <Card className="card-elevated border-2 border-primary">
        <CardContent className="p-6 space-y-4">
          <div className="text-center">
            <Fingerprint className="h-10 w-10 mx-auto text-primary mb-2" />
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Timbratura con impronta</div>
            <div className="text-xs text-muted-foreground mt-1">Le ore vengono calcolate automaticamente da entrata-uscita</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cantiere</label>
            <Select value={cantiereId} onValueChange={setCantiereId}>
              <SelectTrigger data-testid="op-cantiere" className="h-12"><SelectValue placeholder="Scegli cantiere" /></SelectTrigger>
              <SelectContent>{cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="timbra-entrata" onClick={() => timbraBiometrica("entrata")} disabled={busy || !!lastEntrata} className="h-14 bg-emerald-600 hover:bg-emerald-700">
              <LogIn className="h-5 w-5 mr-2" /> Entrata
            </Button>
            <Button data-testid="timbra-uscita" onClick={() => timbraBiometrica("uscita")} disabled={busy || !lastEntrata || !!lastUscita} variant="outline" className="h-14">
              <LogOutIcon className="h-5 w-5 mr-2" /> Uscita
            </Button>
          </div>
          {lastEntrata && (
            <div className="text-xs text-center text-muted-foreground">
              Entrata: <span className="font-bold">{lastEntrata.ora}</span>
              {lastEntrata.biometric_verified && <span className="ml-1 text-emerald-600">✓</span>}
              {lastUscita && <> · Uscita: <span className="font-bold">{lastUscita.ora}</span>{lastUscita.biometric_verified && <span className="ml-1 text-emerald-600">✓</span>}</>}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <Card className="card-elevated"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ore oggi</div>
          <div className="text-2xl font-extrabold"><Clock className="h-4 w-4 inline mr-1" />{oreOggi}h</div>
          <div className="text-xs text-primary font-semibold mt-1">{fmt(pagaOggi)}</div>
        </CardContent></Card>
        <Card className="card-elevated"><CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Da ricevere</div>
          <div className="text-2xl font-extrabold text-amber-600"><Euro className="h-4 w-4 inline" />{fmt(Math.max(0, totMese - totPagato))}</div>
          <div className="text-xs text-muted-foreground mt-1">su {fmt(totMese)}</div>
        </CardContent></Card>
      </div>

      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Timbrature recenti</div>
          <div className="space-y-2">
            {timbrature.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {t.tipo === "entrata" ? <LogIn className="h-3 w-3 text-emerald-600" /> : <LogOutIcon className="h-3 w-3 text-red-600" />}
                    {t.tipo === "entrata" ? "Entrata" : "Uscita"}
                    {t.biometric_verified && <Fingerprint className="h-3 w-3 text-emerald-600" />}
                    {t.corretta_da_admin && <span className="text-[10px] text-amber-600">corretta</span>}
                    {t.anomalia && !t.anomalia.risolta && <AlertTriangle className="h-3 w-3 text-destructive" />}
                  </div>
                  <div className="text-xs text-muted-foreground">{cmap[t.cantiere_id] || "—"} · {t.data} · {t.ora}</div>
                </div>
                {!t.anomalia?.risolta && !t.anomalia && (
                  <Button variant="ghost" size="sm" onClick={() => setAnom({ open: true, tid: t.id, msg: "" })} data-testid={`report-${t.id}`}>
                    <AlertTriangle className="h-3 w-3 text-amber-600" />
                  </Button>
                )}
              </div>
            ))}
            {timbrature.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">Nessuna timbratura.</div>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={anom.open} onOpenChange={(o) => setAnom({ ...anom, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Segnala errore timbratura</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Spiega all'amministratore cosa non torna (ora sbagliata, cantiere errato...)</p>
          <Textarea rows={4} data-testid="report-msg" value={anom.msg} onChange={(e) => setAnom({ ...anom, msg: e.target.value })} placeholder="Es: ho timbrato alle 6:00 ma risulta 6:30" />
          <Button data-testid="report-send" onClick={sendAnomalia} disabled={!anom.msg.trim()}>Invia segnalazione</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
